import { useEffect, useId, useState, type JSX } from 'react';
import {
  CONTENT_LABELS,
  GO_LIVE_NOTIFICATION_MAX,
  STREAM_CATEGORY_MAX,
  STREAM_TAG_PATTERN,
  STREAM_TAGS_MAX,
  STREAM_TITLE_MAX,
  type ContentLabel,
  type StreamInfo,
} from '../../../core/model';
import { CategoryField } from '../../components/CategoryField';
import { Icon } from '../../components/Icon';
import { useProject } from '../../context';
import '../dialogs.css';

export const CONTENT_LABEL_NAMES: Record<ContentLabel, string> = {
  'mature-themes': 'Mature themes',
  'violence-gore': 'Violence and gore',
  'sexual-themes': 'Sexual themes',
  'drugs-intoxication': 'Drugs and intoxication',
  gambling: 'Gambling',
  profanity: 'Profanity',
};

export const STREAM_LANGUAGES: readonly [string, string][] = [
  ['en', 'English'],
  ['es', 'Spanish'],
  ['pt', 'Portuguese'],
  ['pt-BR', 'Portuguese (Brazil)'],
  ['fr', 'French'],
  ['de', 'German'],
  ['it', 'Italian'],
  ['ja', 'Japanese'],
  ['ko', 'Korean'],
  ['zh', 'Chinese'],
  ['ru', 'Russian'],
  ['ar', 'Arabic'],
  ['hi', 'Hindi'],
  ['tr', 'Turkish'],
  ['pl', 'Polish'],
  ['nl', 'Dutch'],
];

export interface CountedTextProps {
  id?: string;
  label: string;
  value: string;
  max: number;
  onCommit(value: string): void;
  /** Report every keystroke instead of committing on blur or Enter (draft forms). */
  live?: boolean;
  multiline?: boolean;
  placeholder?: string;
}

/** A text box with an `n/max` counter that commits on blur or Enter, or on every keystroke when `live`. */
export function CountedText({ id, label, value, max, onCommit, live, multiline, placeholder }: CountedTextProps): JSX.Element {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = (): void => {
    if (draft !== value) onCommit(draft);
  };
  const change = (next: string): void => {
    setDraft(next);
    if (live) onCommit(next);
  };
  const common = {
    id,
    'aria-label': label,
    placeholder,
    maxLength: max,
    value: draft,
    onBlur: live ? undefined : commit,
  };
  return (
    <div className="ui-col" style={{ gap: 2 }}>
      {multiline ? (
        <textarea {...common} className="ui-textarea" style={{ width: '100%' }} onChange={(e) => change(e.target.value)} />
      ) : (
        <input
          {...common}
          className="ui-input"
          style={{ width: '100%' }}
          autoComplete="off"
          onChange={(e) => change(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !live) commit();
          }}
        />
      )}
      <span className="ui-dim stream-info-counter" aria-label={`${label} length`}>
        {draft.length}/{max}
      </span>
    </div>
  );
}

/** Why `tag` cannot be added to `tags`, or null when it can. */
export function tagProblem(tag: string, tags: readonly string[]): string | null {
  if (!tag) return 'Type a tag first.';
  if (!STREAM_TAG_PATTERN.test(tag)) return 'Tags are 1–25 letters, digits or underscores, without spaces.';
  if (tags.some((t) => t.toLowerCase() === tag.toLowerCase())) return 'That tag is already added.';
  if (tags.length >= STREAM_TAGS_MAX) return `At most ${STREAM_TAGS_MAX} tags.`;
  return null;
}

function TagEditor({ tags, onChange }: { tags: string[]; onChange(next: string[]): void }): JSX.Element {
  const [text, setText] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const add = (): void => {
    const tag = text.trim();
    const why = tagProblem(tag, tags);
    setProblem(why);
    if (why) return;
    onChange([...tags, tag]);
    setText('');
  };
  return (
    <div className="ui-col" style={{ gap: 4 }}>
      <div className="ui-row">
        <input
          className={problem ? 'ui-input invalid ui-grow' : 'ui-input ui-grow'}
          aria-label="New tag"
          aria-invalid={problem ? true : undefined}
          placeholder="Tag"
          autoComplete="off"
          value={text}
          disabled={tags.length >= STREAM_TAGS_MAX}
          onChange={(e) => {
            setText(e.target.value);
            setProblem(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className="ui-btn" onClick={add} disabled={tags.length >= STREAM_TAGS_MAX}>
          <Icon name="plus" /> Add tag
        </button>
        <span className="ui-dim" aria-label="Tag count">
          {tags.length}/{STREAM_TAGS_MAX}
        </span>
      </div>
      {problem ? <span className="ui-warn-text">{problem}</span> : null}
      {tags.length > 0 ? (
        <ul className="stream-tags" aria-label="Tags">
          {tags.map((tag) => (
            <li key={tag} className="stream-tag">
              <span>{tag}</span>
              <button type="button" className="stream-tag-remove" aria-label={`Remove tag ${tag}`} title="Remove" onClick={() => onChange(tags.filter((t) => t !== tag))}>
                <Icon name="close" size={10} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export interface StreamInfoFormProps {
  value: StreamInfo;
  onChange(next: StreamInfo): void;
  /** Text fields report every keystroke (a draft held by a dialog) instead of committing on blur or Enter. */
  live?: boolean;
}

/**
 * The fields of `StreamInfo`. Purely controlled: the Channel settings tab commits each change to
 * the project, the Edit stream info dialog keeps them in a draft until Done.
 */
export function StreamInfoForm({ value, onChange, live }: StreamInfoFormProps): JSX.Element {
  const uid = useId();
  const apiUrl = useProject((p) => p.output.streaming.swarmApiUrl);
  const set = (patch: Partial<StreamInfo>): void => onChange({ ...value, ...patch });
  const toggleLabel = (label: ContentLabel, on: boolean): void =>
    set({ contentLabels: CONTENT_LABELS.filter((l) => (l === label ? on : value.contentLabels.includes(l))) });

  return (
    <div className="ui-col stream-info-form" style={{ gap: 8 }}>
      <div className="ui-field">
        <label htmlFor={`${uid}-title`}>Title</label>
        <CountedText id={`${uid}-title`} label="Stream title" value={value.title} max={STREAM_TITLE_MAX} live={live} onCommit={(title) => set({ title })} />
      </div>
      <div className="ui-field">
        <label htmlFor={`${uid}-notify`}>Go-live notification</label>
        <CountedText
          id={`${uid}-notify`}
          label="Go-live notification"
          value={value.goLiveNotification}
          max={GO_LIVE_NOTIFICATION_MAX}
          live={live}
          onCommit={(goLiveNotification) => set({ goLiveNotification })}
        />
        <span className="ui-field-help">Kept with the collection for the follower alert; ompeer.com does not send it yet.</span>
      </div>
      <div className="ui-field">
        <label htmlFor={`${uid}-category`}>Category</label>
        <CategoryField
          id={`${uid}-category`}
          label="Category"
          value={value.category}
          apiUrl={apiUrl}
          max={STREAM_CATEGORY_MAX}
          live={live}
          placeholder="Just Chatting"
          onCommit={(category) => set({ category })}
        />
      </div>
      <div className="ui-field">
        <span className="ui-field-label">Tags</span>
        <TagEditor tags={value.tags} onChange={(tags) => set({ tags })} />
      </div>
      <div className="ui-field">
        <label htmlFor={`${uid}-language`}>Language</label>
        <select id={`${uid}-language`} className="ui-select" aria-label="Stream language" value={value.language} onChange={(e) => set({ language: e.target.value })}>
          <option value="">Not set</option>
          {STREAM_LANGUAGES.map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>
      </div>
      <div className="ui-field" style={{ alignItems: 'start' }}>
        <span className="ui-field-label">Content classification</span>
        <div className="ui-col" role="group" aria-label="Content classification" style={{ gap: 2 }}>
          {CONTENT_LABELS.map((label) => (
            <label key={label} className="ui-check">
              <input type="checkbox" checked={value.contentLabels.includes(label)} onChange={(e) => toggleLabel(label, e.target.checked)} />
              {CONTENT_LABEL_NAMES[label]}
            </label>
          ))}
        </div>
        <span className="ui-field-help">Viewers get a warning with these labels before the stream, its chat and its clips.</span>
      </div>
      <div className="ui-field">
        <span className="ui-field-label">Rerun</span>
        <label className="ui-check">
          <input type="checkbox" checked={value.rerun} onChange={(e) => set({ rerun: e.target.checked })} />
          This broadcast is a rerun
        </label>
        <span className="ui-field-help">Kept with the collection; ompeer.com does not show it yet.</span>
      </div>
    </div>
  );
}
